import { AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChildren, ElementRef, EventEmitter, HostBinding, Injector, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { CsTabContent } from './cs-tab-content';
import { DestroyGuard, getTransitionSlideInOutMs, waitForAngularUpdate } from 'services/browser-utils';
import { isPlatformIOS } from 'environments/environment-helpers';
import { AnimationController } from '../../providers/animation-controller';

@Component({
  selector: 'cs-tab-content-container',
  templateUrl: 'cs-tab-content-container.html',
  host: { 'class': 'tab-content-container' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsTabContentContainer implements AfterContentInit, OnChanges, OnDestroy {
  private destroyGuard = new DestroyGuard();
  @Input() animate: boolean = true;
  @Input() selection: any;
  // optional: override auto-detection of direction (for recycling slot patterns like A/B/A/B)
  @Input() isBack: boolean | undefined;
  @Input() swipeEnabled: boolean = false;
  @Output() swipeForward = new EventEmitter<void>();
  @Output() swipeBack = new EventEmitter<void>();

  @ContentChildren(CsTabContent) contents: QueryList<CsTabContent>;

  private contentsSubscription: Subscription | null = null;

  private isInitialized: boolean = false;
  // flag to skip animation on initial render
  private _skipInitialAnimation: boolean = true;
  // timer for clearing leaving state after animation
  private animationCleanupTimer: any = null;
  // track if animation is in progress
  private isAnimating: boolean = false;
  // queued selection to process after current animation completes
  private queuedSelection: { value: any; isBack: boolean | undefined } | null = null;

  // swipe gesture state
  private swipeStartX: number = 0;
  private swipeStartY: number = 0;
  private swipeIsVertical: boolean = false;
  private swipeDirectionLocked: boolean = false;
  private readonly SWIPE_THRESHOLD = 50;
  private readonly EDGE_EXCLUSION_ZONE = 20;
  private swipeIgnored: boolean = false;
  private touchStartHandler = (e: TouchEvent) => this.onTouchStart(e);
  private touchMoveHandler = (e: TouchEvent) => this.onTouchMove(e);
  private touchEndHandler = (e: TouchEvent) => this.onTouchEnd(e);

  constructor(private cdr: ChangeDetectorRef, private injector: Injector, private elementRef: ElementRef, private animationController: AnimationController) {}

  @HostBinding('class.no-animate')
  get noAnimate(): boolean {
    // disable animation if: animate input is false OR we're in initial render
    return !this.animate || this._skipInitialAnimation;
  }

  ngAfterContentInit() {
    // if selection is provided, set initial active state without animation
    if (this.selection !== undefined) {
      this.setInitialState();
    }
    this.isInitialized = true;

    // subscribe to content changes (handles dynamically added content via *ngIf)
    this.contentsSubscription = this.contents.changes.subscribe(() => {
      if (this.destroyGuard.isDestroyed) return;
      if (this.selection !== undefined && this.contents.length > 0) {
        // temporarily disable animation for initial state setup
        this._skipInitialAnimation = true;
        this.setInitialState();
        this.cdr.markForCheck();

        // re-enable animation after render
        void (async () => {
          await waitForAngularUpdate(this.injector);
          if (this.destroyGuard.isDestroyed) return;
          this._skipInitialAnimation = false;
          this.cdr.markForCheck();
        })();
      }
    });

    // enable animation after initial render completes
    void (async () => {
      await waitForAngularUpdate(this.injector);
      if (this.destroyGuard.isDestroyed) return;
      this._skipInitialAnimation = false;
      this.cdr.markForCheck();
    })();
  }

  ngOnChanges(changes: SimpleChanges) {
    // if swipeEnabled changed, setup or teardown listeners
    if (changes['swipeEnabled']) {
      if (this.swipeEnabled) {
        this.setupSwipeListeners();
      } else {
        this.teardownSwipeListeners();
      }
    }

    // if selection changed and we're initialized
    if (changes['selection'] && this.isInitialized && this.contents) {
      const prev = changes['selection'].previousValue;
      const curr = changes['selection'].currentValue;
      if (prev !== curr) {
        // if selection transitions from undefined to a value, set initial state first
        if (prev === undefined && curr !== undefined) {
          this._skipInitialAnimation = true;
          this.setInitialState();
          this.cdr.markForCheck();

          // re-enable animation after render
          void (async () => {
            await waitForAngularUpdate(this.injector);
            if (this.destroyGuard.isDestroyed) return;
            this._skipInitialAnimation = false;
            this.cdr.markForCheck();
          })();
        } else {
          this.handleSelectionChange(prev, curr);
        }
      }
    }
  }

  ngOnDestroy() {
    this.destroyGuard.destroy();
    this.teardownSwipeListeners();
    if (this.contentsSubscription) {
      this.contentsSubscription.unsubscribe();
    }
    if (this.animationCleanupTimer) {
      clearTimeout(this.animationCleanupTimer);
    }
    // if destroyed mid-animation, balance the animation counter
    if (this.isAnimating) {
      this.isAnimating = false;
      this.animationController.onAnimationEnded();
    }
  }

  // region swipe gesture handling

  private setupSwipeListeners() {
    const el = this.elementRef.nativeElement as HTMLElement;
    el.addEventListener('touchstart', this.touchStartHandler, { passive: true });
    el.addEventListener('touchmove', this.touchMoveHandler, { passive: false });
    el.addEventListener('touchend', this.touchEndHandler, { passive: true });
  }

  private teardownSwipeListeners() {
    const el = this.elementRef.nativeElement as HTMLElement;
    el.removeEventListener('touchstart', this.touchStartHandler);
    el.removeEventListener('touchmove', this.touchMoveHandler);
    el.removeEventListener('touchend', this.touchEndHandler);
  }

  private isInsideHorizontalScroller(target: EventTarget | null): boolean {
    let el = target as HTMLElement | null;
    const container = this.elementRef.nativeElement as HTMLElement;
    while (el && el !== container) {
      if (el.scrollWidth > el.clientWidth) {
        const overflow = getComputedStyle(el).overflowX;
        if (overflow === 'scroll' || overflow === 'auto') return true;
      }
      el = el.parentElement;
    }
    return false;
  }

  private onTouchStart(e: TouchEvent) {
    // only handle single finger
    if (e.touches.length !== 1) return;
    const x = e.touches[0].clientX;

    // ignore touches near screen edges to avoid conflicting with iOS swipe-back gesture
    if (x < this.EDGE_EXCLUSION_ZONE || x > window.innerWidth - this.EDGE_EXCLUSION_ZONE) {
      this.swipeIgnored = true;
      return;
    }

    // ignore touches inside horizontally scrollable children (e.g. image carousels)
    if (this.isInsideHorizontalScroller(e.target)) {
      this.swipeIgnored = true;
      return;
    }

    this.swipeIgnored = false;
    this.swipeStartX = x;
    this.swipeStartY = e.touches[0].clientY;
    this.swipeIsVertical = false;
    this.swipeDirectionLocked = false;
  }

  private onTouchMove(e: TouchEvent) {
    if (this.swipeIgnored || e.touches.length !== 1) return;

    // if already locked as horizontal, prevent scroll and stop bubbling to parent containers
    if (this.swipeDirectionLocked && !this.swipeIsVertical) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (this.swipeDirectionLocked) return;

    const deltaX = Math.abs(e.touches[0].clientX - this.swipeStartX);
    const deltaY = Math.abs(e.touches[0].clientY - this.swipeStartY);

    // lock direction once enough movement is detected
    if (deltaX > 10 || deltaY > 10) {
      this.swipeIsVertical = deltaY > deltaX;
      this.swipeDirectionLocked = true;

      // if horizontal, prevent scroll and stop bubbling immediately
      if (!this.swipeIsVertical) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }

  private onTouchEnd(e: TouchEvent) {
    if (this.swipeIgnored || this.swipeIsVertical || !this.swipeDirectionLocked) return;

    // horizontal swipe belongs to this container — stop bubbling to parent containers
    e.stopPropagation();

    if (this.isAnimating) return;
    const deltaX = e.changedTouches[0].clientX - this.swipeStartX;

    if (Math.abs(deltaX) < this.SWIPE_THRESHOLD) return;

    // swipe left (negative delta) = forward, swipe right (positive delta) = back
    if (deltaX < 0) {
      this.swipeForward.emit();
    } else {
      this.swipeBack.emit();
    }
  }

  // endregion

  private setInitialState() {
    // set initial state without animation for all content with values
    this.contents.forEach(content => {
      if (content.value !== undefined) {
        const isActive = content.value === this.selection;
        content._setAnimationState({
          active: isActive,
          entering: false,
          enteringBack: false,
          leaving: false,
          leavingBack: false
        });
      }
    });
  }

  private handleSelectionChange(oldValue: any, newValue: any) {
    // if animation is in progress, queue this selection and return
    if (this.isAnimating) {
      this.queuedSelection = { value: newValue, isBack: this.isBack };
      return;
    }

    // cancel any pending animation cleanup from previous transition
    if (this.animationCleanupTimer) {
      clearTimeout(this.animationCleanupTimer);
      this.animationCleanupTimer = null;
    }

    this.isAnimating = true;
    this.animationController.onAnimationStarted();

    const contentsArray = this.contents.toArray().filter(c => c.value !== undefined);

    // find indices based on DOM order (ContentChildren query order)
    const oldIndex = contentsArray.findIndex(c => c.value === oldValue);
    const newIndex = contentsArray.findIndex(c => c.value === newValue);

    // if either index not found, skip directional animation
    if (oldIndex === -1 || newIndex === -1) {
      // just set active state directly
      contentsArray.forEach(content => {
        content._setAnimationState({
          active: content.value === newValue,
          entering: false,
          enteringBack: false,
          leaving: false,
          leavingBack: false
        });
      });
      this.isAnimating = false;
      this.animationController.onAnimationEnded();
      return;
    }

    // use provided isBack if available, otherwise auto-detect from DOM order
    const isBack = this.isBack !== undefined ? this.isBack : newIndex < oldIndex;
    const oldContent = contentsArray[oldIndex];
    const newContent = contentsArray[newIndex];

    // Phase 1: Set up directional states (position new content, mark old as leaving)
    // Key: new content gets entering/enteringBack but NOT active yet (positions at +100%/-100%)
    oldContent._setAnimationState({
      active: false,
      entering: false,
      enteringBack: false,
      leaving: !isBack,
      leavingBack: isBack
    });

    newContent._setAnimationState({
      active: false,  // NOT active yet - this is the key difference from before!
      entering: !isBack,
      enteringBack: isBack,
      leaving: false,
      leavingBack: false
    });

    this.cdr.markForCheck();

    // Phase 2: After Angular renders the positioning, trigger the animation
    void (async () => {
      await waitForAngularUpdate(this.injector);
      if (this.destroyGuard.isDestroyed) return;

      // #4410: while the arriving panel is still parked off-screen, force iOS
      // to rebuild its compositor layer. Without this, a reused panel can
      // arrive with a stale layer and paint nothing — a blank day on the
      // Training page — even though its DOM, styles and geometry are all
      // correct. Costs ~3 frames before the slide starts, off-screen and
      // invisible. iOS-only (a WebKit-on-iOS compositor problem) and opt-in
      // per panel (see CsTabContent.rebuildLayerOnEnter). Details and measured
      // dead ends: design-docs/navigation.md, "iOS stale-layer rebuild on tab
      // switch".
      if (newContent.rebuildLayerOnEnter && isPlatformIOS()) {
        await newContent._rebuildLayerWhileHiddenAsync();
        if (this.destroyGuard.isDestroyed) return;
      }

      // now set active and clear entering/enteringBack - this triggers the animation
      newContent._setAnimationState({
        active: true,
        entering: false,
        enteringBack: false,
        leaving: false,
        leavingBack: false
      });
      this.cdr.markForCheck();

      // clear leaving state after animation completes
      this.animationCleanupTimer = setTimeout(() => {
        if (this.destroyGuard.isDestroyed) return;
        // if there's a queued selection, start the new animation immediately
        // DON'T clear oldContent's leaving state - it's off-screen and will be cleaned up
        // when it becomes active/entering in a future animation
        if (this.queuedSelection !== null) {
          const queued = this.queuedSelection;
          this.queuedSelection = null;
          this.animationCleanupTimer = null;
          this.isAnimating = false;
          this.animationController.onAnimationEnded();
          // temporarily set isBack for the queued animation
          const prevIsBack = this.isBack;
          this.isBack = queued.isBack;
          this.handleSelectionChange(newValue, queued.value);
          this.isBack = prevIsBack;
          return;
        }

        oldContent._setAnimationState({
          active: false,
          entering: false,
          enteringBack: false,
          leaving: false,
          leavingBack: false
        });
        this.cdr.markForCheck();
        this.animationCleanupTimer = null;
        this.isAnimating = false;
        this.animationController.onAnimationEnded();
      }, getTransitionSlideInOutMs());
    })();
  }
}
